import React, { useState, useEffect } from 'react';
import { Search, Filter, CheckCircle, XCircle, Eye } from 'lucide-react';
import { db } from '../services/firebase';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  query,
  where
} from 'firebase/firestore';
import './ProductApproval.css';
import Layout from '../component/Layout';

const OpenMarketOrdersApproval = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('requested');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [productToReject, setProductToReject] = useState(null);

  // Fetch ALL products from Firestore
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        
         const productsRef = collection(db, 'products');
         const q = query(productsRef, where('serviceType', "==", "openmarket"));

        const querySnapshot = await getDocs(q);
        const productsData = [];

        console.log(`Found ${querySnapshot.docs.length} products`);

        for (const docSnap of querySnapshot.docs) {
          const product = { id: docSnap.id, ...docSnap.data() };
          console.log(docSnap.id);
          productsData.push(product);
        }

        setProducts(productsData);
        setFilteredProducts(productsData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching products:', error);
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Filter products based on search term and status filter
  useEffect(() => {
    let filtered = products;

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(product => product.status === statusFilter);
    }

    // Apply search term filter
    if (searchTerm !== '') {
      filtered = filtered.filter(product =>
        product.productName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.segment?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.status?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredProducts(filtered);
  }, [searchTerm, statusFilter, products]);

  const handleApprove = async (productId) => {
    try {
      const productRef = doc(db, 'products', productId);
      await updateDoc(productRef, {
        status: 'approved',
        approvedAt: new Date()
      });
      setProducts(prev => prev.map(product =>
        product.id === productId
          ? { ...product, status: 'approved', approvedAt: new Date() }
          : product
      ));
    } catch (error) {
      console.error('Error approving product:', error);
    }
  };

  const openRejectModal = (productId) => {
    setProductToReject(productId);
    setShowRejectModal(true);
  };

  const closeRejectModal = () => {
    setShowRejectModal(false);
    setRejectReason('');
    setProductToReject(null);
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      return;
    }

    try {
      const productRef = doc(db, 'products', productToReject);
      await updateDoc(productRef, {
        status: 'rejected',
        rejectionReason: rejectReason,
        rejectedAt: new Date()
      });

      // Update local state
      setProducts(prev => prev.map(product =>
        product.id === productToReject
          ? { ...product, status: 'rejected', rejectionReason: rejectReason, rejectedAt: new Date() }
          : product
      ));

      closeRejectModal();
    } catch (error) {
      console.error('Error rejecting product:', error);
    }
  };

  const viewProductDetails = (product) => {
    setSelectedProduct(product);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedProduct(null);
  };

  if (loading) {
    return (
      <div className="operator-product-approval">
        <div className="loading-container">Loading products...</div>
      </div>
    );
  }

  return (
    <Layout pageTitle="Product Approval" >
      <div className="operator-product-approval">
        <div className="approval-header">
          <h1>Open Market Orders Approval</h1>
          <div className="controls">
            <div className="search-box">
              <Search size={20} />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="filter-group">
              <Filter size={20} />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="requested">Requested</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>

        <div className="products-container">
          <div className="products-grid">
            {filteredProducts.length === 0 ? (
              <div className="no-products">
                No products found
              </div>
            ) : (
              filteredProducts.map((product) => (
                <div key={product.id} className="product-card">
                  <div className="product-image">
                    {product.images && product.images.length > 0 && (
                      <img
                        src={product.images[0].url}
                        alt={product.productName}
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/200x200?text=No+Image';
                        }}
                      />
                    )}
                    {(!product.images || product.images.length === 0) && (
                      <div className="no-image">No Image</div>
                    )}
                  </div>

                  <div className="product-info">
                    <h3 className="product-name">{product.productName || 'N/A'}</h3>
                    <div className="product-category">
                      {product.segment} - {product.category}
                    </div>
                    <div className="product-details">
                      <span>Gross: {product.grossWt}g</span>
                      <span>Net: {product.netWt}g</span>
                    </div>
                    <div className="product-status">
                      <span className={`status-badge ${product.status}`}>
                        {product.status}
                      </span>
                    </div>
                  </div>

                  <div className="product-actions">
                    <button
                      onClick={() => viewProductDetails(product)}
                      className="view-btn"
                      title="View Details"
                    >
                      <Eye size={16} />
                    </button>

                    {product.status === 'requested' && (
                      <>
                        <button
                          onClick={() => handleApprove(product.id)}
                          className="approve-btn"
                          title="Approve"
                        >
                          <CheckCircle size={16} />
                        </button>
                        <button
                          onClick={() => openRejectModal(product.id)}
                          className="reject-btn"
                          title="Reject"
                        >
                          <XCircle size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Product Detail Modal */}
        {showModal && selectedProduct && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Product Details</h2>
                <button className="close-btn" onClick={closeModal}>×</button>
              </div>

              <div className="modal-body">
                <div className="product-images">
                  {selectedProduct.images && selectedProduct.images.length > 0 ? (
                    selectedProduct.images.map((image, index) => (
                      <img
                        key={index}
                        src={image.url}
                        alt={`Product ${index + 1}`}
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/200x200?text=Image+Error';
                        }}
                      />
                    ))
                  ) : (
                    <div className="no-images">No images available</div>
                  )}
                </div>

                <div className="details-grid">
                  <div className="detail-group">
                    <h3>Product Information</h3>
                    <div className="detail-item">
                      <span className="label">Name:</span>
                      <span className="value">{selectedProduct.productName || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Segment:</span>
                      <span className="value">{selectedProduct.segment || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Category:</span>
                      <span className="value">{selectedProduct.category || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Style Type:</span>
                      <span className="value">{selectedProduct.styleType || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Specification:</span>
                      <span className="value">{selectedProduct.specification || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Source:</span>
                      <span className="value">{selectedProduct.productSource || 'N/A'}</span>
                    </div>
                  </div>

                  <div className="detail-group">
                    <h3>Technical Details</h3>
                    <div className="detail-item">
                      <span className="label">Gross Weight:</span>
                      <span className="value">{selectedProduct.grossWt || 'N/A'}g</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Net Weight:</span>
                      <span className="value">{selectedProduct.netWt || 'N/A'}g</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Purity:</span>
                      <span className="value">{selectedProduct.purity || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Wastage:</span>
                      <span className="value">{selectedProduct.wastage || 'N/A'}</span>
                    </div>
                  </div>

                  <div className="detail-group">
                    <h3>Business Details</h3>
                    <div className="detail-item">
                      <span className="label">Service Type:</span>
                      <span className="value">{selectedProduct.serviceType || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">MOQ Gram:</span>
                      <span className="value">{selectedProduct.moqGram || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">MOQ Set:</span>
                      <span className="value">{selectedProduct.moqSet || 'N/A'}</span>
                    </div>
                    {selectedProduct.serviceType === 'ready' && (
                      <>
                        <div className="detail-item">
                          <span className="label">Instock Gram:</span>
                          <span className="value">{selectedProduct.instockGram || 'N/A'}</span>
                        </div>
                        <div className="detail-item">
                          <span className="label">Instock Set:</span>
                          <span className="value">{selectedProduct.instockSet || 'N/A'}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {selectedProduct.status === 'requested' && (
                  <div className="modal-actions">
                    <button
                      onClick={() => {
                        handleApprove(selectedProduct.id);
                        closeModal();
                      }}
                      className="approve-btn large"
                    >
                      Approve Product
                    </button>
                    <button
                      onClick={() => {
                        openRejectModal(selectedProduct.id);
                        closeModal();
                      }}
                      className="reject-btn large"
                    >
                      Reject Product
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Reject Reason Modal */}
        {showRejectModal && (
          <div className="modal-overlay" onClick={closeRejectModal}>
            <div className="modal-content reject-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Reject Product</h2>
                <button className="close-btn" onClick={closeRejectModal}>×</button>
              </div>

              <div className="modal-body">
                <p>Please provide a reason for rejecting this product:</p>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Enter rejection reason..."
                  rows={4}
                  className="reject-reason-input"
                />
                <div className="modal-actions">
                  <button
                    onClick={closeRejectModal}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReject}
                    className="reject-btn large"
                  >
                    Confirm Rejection
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout >
  );
};

export default OpenMarketOrdersApproval;